import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { VerificationAPI, OrderAPI } from '../services/api';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Main Container
const CheckoutContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  color: #1a1a1a;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 2rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

// Header
const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 200;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: #666666;
  font-size: 1rem;
  font-weight: 300;
`;

// Progress Indicator
const ProgressContainer = styled.div`
  margin-bottom: 3rem;
`;

const ProgressSteps = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ProgressStep = styled.div<{ $active: boolean; $completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 15px;
    left: 50%;
    width: 100%;
    height: 2px;
    background: ${props => props.$completed ? '#d4af37' : '#e0e0e0'};
    z-index: -1;
  }
  
  &:last-child::after {
    display: none;
  }
`;

const StepNumber = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => 
    props.$completed ? '#d4af37' :
    props.$active ? '#1a1a1a' : '#e0e0e0'
  };
  color: ${props => 
    props.$completed || props.$active ? '#ffffff' : '#999999'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
`;

const StepLabel = styled.span<{ $active: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$active ? '#1a1a1a' : '#999999'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
`;

// Checkout Layout
const CheckoutLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

// Form Section
const FormSection = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 300;
  margin-bottom: 2rem;
  color: #1a1a1a;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
    color: #999999;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.875rem;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
    color: #999999;
  }
`;

// Payment Methods
const PaymentMethods = styled.div`
  margin-top: 2rem;
`;

const PaymentMethod = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
  }
  
  &.selected {
    border-color: #d4af37;
    background: rgba(212, 175, 55, 0.05);
  }
`;

const RadioInput = styled.input`
  margin: 0;
`;

const PaymentIcon = styled.div`
  font-size: 1.5rem;
`;

const PaymentInfo = styled.div`
  flex: 1;
`;

const PaymentName = styled.div`
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
`;

const PaymentDescription = styled.div`
  font-size: 0.75rem;
  color: #666666;
`;

// Order Summary
const OrderSummary = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const SummaryTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  color: #1a1a1a;
`;

const SummaryItems = styled.div`
  margin-bottom: 1.5rem;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ItemImage = styled.div`
  width: 50px;
  height: 50px;
  background: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
`;

const ItemOptions = styled.div`
  font-size: 0.75rem;
  color: #666666;
`;

const ItemPrice = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1a1a1a;
`;

const SummaryTotals = styled.div`
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  
  &.total {
    font-weight: 600;
    font-size: 1rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e0e0e0;
  }
`;

const TotalLabel = styled.span`
  color: #666666;
`;

const TotalValue = styled.span`
  color: #1a1a1a;
`;

// Action Buttons
const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const BackButton = styled.button`
  flex: 1;
  background: transparent;
  color: #666666;
  border: 1px solid #e0e0e0;
  padding: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;
  
  &:hover {
    border-color: #1a1a1a;
    color: #1a1a1a;
  }
`;

const ContinueButton = styled.button`
  flex: 1;
  background: #1a1a1a;
  color: #ffffff;
  border: none;
  padding: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;
  
  &:hover {
    background: #d4af37;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(26, 26, 26, 0.2);
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Pop-up Overlay
const PopupOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const PopupContent = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  position: relative;
`;

const PopupIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  animation: bounce 1s ease infinite;
  
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
`;

const PopupTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
`;

const PopupDescription = styled.p`
  color: #666666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CustomerIdBox = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
`;

const CustomerIdLabel = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.75rem;
`;

const CustomerIdValue = styled.div`
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  font-family: 'Courier New', monospace;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const PopupButton = styled.button`
  background: #1a1a1a;
  color: #ffffff;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  &:hover {
    background: #d4af37;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(212, 175, 55, 0.3);
  }
`;

// Sample Data - Bu artık gerçek sepet verilerinden gelecek

const paymentMethods = [
  {
    id: 'credit',
    name: 'Credit Card',
    description: 'Visa, Mastercard, American Express',
    icon: '💳'
  },
  {
    id: 'paypal',
    name: 'PayPal',
    description: 'Pay with your PayPal account',
    icon: '🅿️'
  },
  {
    id: 'bank',
    name: 'Bank Transfer',
    description: 'Direct bank transfer',
    icon: '🏦'
  }
];

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useCart();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [ibanInfo, setIbanInfo] = useState<any>(null);
  const [ibanAccounts, setIbanAccounts] = useState<any[]>([]);
  const [orderResponse, setOrderResponse] = useState<any>(null);
  const [showCustomerIdPopup, setShowCustomerIdPopup] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    notes: ''
  });

  const steps = [
    { id: 1, label: 'Address' },
    { id: 2, label: 'Email' },
    { id: 3, label: 'Payment' },
    { id: 4, label: 'Confirm' }
  ];

  const getSubtotal = () => {
    return cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  };

  const getShipping = () => {
    return getSubtotal() > 5000 ? 0 : 150;
  };

  const getTotal = () => {
    return getSubtotal() + getShipping();
  };

  const formatPrice = (price: number) => {
    return `₺${price.toLocaleString()}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleNext = async () => {
    setError('');
    
    if (currentStep === 1) {
      // Tüm gerekli alanları kontrol et
      if (!formData.firstName || !formData.lastName || !formData.email || 
          !formData.phone || !formData.address || !formData.city || 
          !formData.postalCode || !formData.country) {
        setError('Lütfen tüm alanları doldurun');
        return;
      }
      
      // Email formatını kontrol et
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Geçerli bir email adresi girin');
        return;
      }
      
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Doğrulama kodu gönder
      setLoading(true);
      try {
        await VerificationAPI.sendVerificationCode(formData.email);
        setCurrentStep(3);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 3) {
      // Doğrulama kodunu kontrol et
      if (!verificationCode) {
        setError('Doğrulama kodu gerekli');
        return;
      }
      setLoading(true);
      try {
        const response = await VerificationAPI.verifyCode(verificationCode);
        setIbanInfo(response.iban_info);
        setIbanAccounts(response.iban_accounts || [response.iban_info]);
        
        // Siparişi oluştur
        const orderData = {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          country: formData.country,
          total_amount: getTotal(),
        };
        
        const orderResult = await OrderAPI.createOrder(orderData);
        setOrderResponse(orderResult);
        clearCart();
        
        // Pop-up'ı göster
        setShowCustomerIdPopup(true);
        
        setCurrentStep(4);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 4) {
      // Siparişlerim sayfasına yönlendir
      navigate('/orders');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FormSection>
            <FormTitle>İletişim Bilgileri</FormTitle>
            <FormRow>
              <FormGroup>
                <Label>Ad</Label>
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Adınızı girin"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Soyad</Label>
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Soyadınızı girin"
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormRow>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email adresinizi girin"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Telefon</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Telefon numaranızı girin"
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label>Adres</Label>
              <Input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Adresinizi girin"
                required
              />
            </FormGroup>
            
            <FormRow>
              <FormGroup>
                <Label>Şehir</Label>
                <Input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Şehrinizi girin"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Posta Kodu</Label>
                <Input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="Posta kodunuzu girin"
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label>Ülke</Label>
              <Select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                required
              >
                <option value="">Ülkenizi seçin</option>
                <option value="TR">Türkiye</option>
                <option value="US">Amerika Birleşik Devletleri</option>
                <option value="UK">Birleşik Krallık</option>
                <option value="DE">Almanya</option>
                <option value="FR">Fransa</option>
              </Select>
            </FormGroup>
          </FormSection>
        );
        
      case 2:
        return (
          <FormSection>
            <FormTitle>Email Doğrulama</FormTitle>
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📧</div>
              <h3 style={{ marginBottom: '1rem', color: '#1a1a1a' }}>Email Adresinizi Doğrulayın</h3>
              <p style={{ color: '#666666', marginBottom: '2rem' }}>
                <strong>{formData.email}</strong> adresine doğrulama kodu gönderilecek.
              </p>
              <p style={{ color: '#666666', fontSize: '0.9rem' }}>
                Doğrulama kodu 10 dakika geçerlidir.
              </p>
            </div>
          </FormSection>
        );
        
      case 3:
        return (
          <FormSection>
            <FormTitle>Doğrulama Kodu</FormTitle>
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔐</div>
              <h3 style={{ marginBottom: '1rem', color: '#1a1a1a' }}>Doğrulama Kodunu Girin</h3>
              <p style={{ color: '#666666', marginBottom: '2rem' }}>
                <strong>{formData.email}</strong> adresine gönderilen 6 haneli kodu girin.
              </p>
              <FormGroup>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  style={{ 
                    textAlign: 'center', 
                    fontSize: '1.5rem', 
                    letterSpacing: '0.5rem',
                    width: '200px',
                    margin: '0 auto'
                  }}
                />
              </FormGroup>
            </div>
          </FormSection>
        );
        
      case 4:
        return (
          <FormSection>
            <FormTitle>Sipariş Tamamlandı</FormTitle>
            {ibanInfo && orderResponse && (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                <h3 style={{ marginBottom: '1rem', color: '#1a1a1a' }}>Siparişiniz Oluşturuldu!</h3>
                
                {/* Müşteri Numarası - ÖNEMLİ */}
                <div style={{ 
                  background: '#e8f5e9', 
                  border: '2px solid #4caf50',
                  padding: '1.5rem', 
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  maxWidth: '500px',
                  margin: '0 auto 2rem auto'
                }}>
                  <div style={{ 
                    fontSize: '0.9rem',
                    color: '#2e7d32',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem'
                  }}>
                    ⚠️ ÖNEMLİ: Müşteri Numaranız
                  </div>
                  <div style={{ 
                    fontSize: '1.5rem',
                    color: '#1b5e20',
                    fontWeight: 'bold',
                    letterSpacing: '2px',
                    padding: '0.5rem',
                    background: '#ffffff',
                    borderRadius: '4px',
                    marginBottom: '0.5rem'
                  }}>
                    {orderResponse.customer_id}
                  </div>
                  <div style={{ 
                    fontSize: '0.85rem',
                    color: '#2e7d32',
                    marginTop: '0.5rem'
                  }}>
                    Bu numarayı kaydedin! Sipariş durumunuzu kontrol etmek için kullanacaksınız.
                  </div>
                </div>

                <div style={{ 
                  background: '#fff3e0', 
                  border: '1px solid #ff9800',
                  padding: '1rem', 
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  maxWidth: '500px',
                  margin: '0 auto 2rem auto',
                  fontSize: '0.9rem',
                  color: '#e65100'
                }}>
                  📋 Sipariş Numarası: <strong>{orderResponse.order_id}</strong>
                </div>
                
                <h3 style={{ marginBottom: '1rem', color: '#1a1a1a', fontSize: '1.2rem' }}>Ödeme Bilgileri</h3>
                {ibanAccounts.map((account, index) => (
                  <div key={index} style={{
                    background: '#f5f5f5',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    margin: '0 auto 1rem auto'
                  }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Banka:</strong> {account.bank_name} {account.currency && `(${account.currency})`}
                    </div>
                    <div style={{ marginBottom: '1rem', wordBreak: 'break-all' }}>
                      <strong>IBAN:</strong> {account.iban}
                    </div>
                    {account.swift && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>SWIFT:</strong> {account.swift}
                      </div>
                    )}
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Hesap Sahibi:</strong> {account.account_holder}
                    </div>
                    <div style={{ 
                      marginBottom: '1rem', 
                      fontSize: '1.2rem',
                      color: '#d4af37',
                      fontWeight: 'bold'
                    }}>
                      <strong>Ödenecek Tutar:</strong> ₺{account.amount}
                    </div>
                  </div>
                ))}
                
                <div style={{ 
                  background: '#e3f2fd', 
                  padding: '1.5rem', 
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  maxWidth: '500px',
                  margin: '0 auto',
                  textAlign: 'left'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#1976d2' }}>
                    📌 Sonraki Adımlar:
                  </div>
                  <ol style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', color: '#424242' }}>
                    <li style={{ marginBottom: '0.5rem' }}>Yukarıdaki IBAN'a ödemenizi yapın</li>
                    <li style={{ marginBottom: '0.5rem' }}>Müşteri numaranızı kaydedin</li>
                    <li style={{ marginBottom: '0.5rem' }}>Sipariş durumunu kontrol etmek için <strong>Siparişlerim</strong> sayfasına gidin</li>
                  </ol>
                </div>
              </div>
            )}
          </FormSection>
        );
        
      default:
        return null;
    }
  };

  // Sepet boşsa ana sayfaya yönlendir
  if (cartItems.length === 0) {
    navigate('/');
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Checkout - Elite Store</title>
        <meta name="description" content="Complete your luxury purchase with secure checkout." />
      </Helmet>
      
      {/* Müşteri Kimlik Numarası Pop-up */}
      <AnimatePresence>
        {showCustomerIdPopup && orderResponse && (
          <PopupOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCustomerIdPopup(false)}
          >
            <PopupContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <PopupIcon>🎉</PopupIcon>
              <PopupTitle>Siparişiniz Oluşturuldu!</PopupTitle>
              <PopupDescription>
                Müşteri kimlik numaranızı kaydetmeyi unutmayın. Bu numarayı sipariş durumunuzu takip etmek için kullanacaksınız.
              </PopupDescription>
              
              <CustomerIdBox>
                <CustomerIdLabel>Müşteri Kimlik Numaranız</CustomerIdLabel>
                <CustomerIdValue>{orderResponse.customer_id}</CustomerIdValue>
              </CustomerIdBox>
              
              <PopupDescription style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
                Bu numara email adresinize de gönderilmiştir.
              </PopupDescription>
              
              <PopupButton onClick={() => {
                setShowCustomerIdPopup(false);
              }}>
                Anladım
              </PopupButton>
            </PopupContent>
          </PopupOverlay>
        )}
      </AnimatePresence>
      
      <CheckoutContainer>
        <Container>
          <Header>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Title>Checkout</Title>
              <Subtitle>Complete your luxury purchase</Subtitle>
            </motion.div>
          </Header>

          <ProgressContainer>
            <ProgressSteps>
              {steps.map((step) => (
                <ProgressStep
                  key={step.id}
                  $active={currentStep === step.id}
                  $completed={currentStep > step.id}
                >
                  <StepNumber
                    $active={currentStep === step.id}
                    $completed={currentStep > step.id}
                  >
                    {currentStep > step.id ? '✓' : step.id}
                  </StepNumber>
                  <StepLabel $active={currentStep === step.id}>
                    {step.label}
                  </StepLabel>
                </ProgressStep>
              ))}
            </ProgressSteps>
          </ProgressContainer>

          <CheckoutLayout>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {renderStepContent()}
              
              {error && (
                <div style={{ 
                  background: '#ffebee', 
                  color: '#c62828', 
                  padding: '1rem', 
                  borderRadius: '4px',
                  margin: '1rem 0',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}
              
              <ActionButtons>
                {currentStep > 1 && currentStep < 4 && (
                  <BackButton onClick={handleBack}>
                    Geri
                  </BackButton>
                )}
                <ContinueButton onClick={handleNext} disabled={loading}>
                  {loading ? 'İşleniyor...' : 
                   currentStep === 1 ? 'Devam Et' :
                   currentStep === 2 ? 'Doğrulama Kodu Gönder' :
                   currentStep === 3 ? 'Siparişi Oluştur' : 
                   currentStep === 4 ? 'Siparişlerime Git' : 'Devam Et'}
                </ContinueButton>
                {currentStep === 4 && (
                  <BackButton onClick={() => navigate('/')} style={{ marginLeft: '1rem' }}>
                    Ana Sayfaya Dön
                  </BackButton>
                )}
              </ActionButtons>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <OrderSummary>
                <SummaryTitle>Order Summary</SummaryTitle>
                
                <SummaryItems>
                  {cartItems.map((item) => (
                    <SummaryItem key={item.id}>
                      <ItemInfo>
                        <ItemImage>
                          <img src={item.product_image} alt={item.product_name} />
                        </ItemImage>
                        <ItemDetails>
                          <ItemName>{item.product_name}</ItemName>
                          <ItemOptions>Miktar: {item.quantity}</ItemOptions>
                        </ItemDetails>
                      </ItemInfo>
                      <ItemPrice>{formatPrice(item.price * item.quantity)}</ItemPrice>
                    </SummaryItem>
                  ))}
                </SummaryItems>
                
                <SummaryTotals>
                  <TotalRow>
                    <TotalLabel>Subtotal</TotalLabel>
                    <TotalValue>{formatPrice(getSubtotal())}</TotalValue>
                  </TotalRow>
                  <TotalRow>
                    <TotalLabel>Shipping</TotalLabel>
                    <TotalValue>
                      {getShipping() === 0 ? 'Free' : formatPrice(getShipping())}
                    </TotalValue>
                  </TotalRow>
                  <TotalRow className="total">
                    <TotalLabel>Total</TotalLabel>
                    <TotalValue>{formatPrice(getTotal())}</TotalValue>
                  </TotalRow>
                </SummaryTotals>
              </OrderSummary>
            </motion.div>
          </CheckoutLayout>
        </Container>
      </CheckoutContainer>
    </>
  );
};

export default Checkout;
